/** 表示公开顾问接口返回的可恢复错误，供前端按 HTTP 状态和稳定错误码处理。 */
class TalismentAdvisorRequestError extends Error {
  /* 创建一个接口错误；入参为 HTTP 状态与稳定错误码，无返回值。 */
  constructor(status, code) {
    super(code || `advisor_request_${status}`);
    this.name = "TalismentAdvisorRequestError";
    this.status = status;
    this.code = code || "advisor_request_failed";
  }
}

class TalismentTraditionalCultureAdvisor {
  /* Initialize one advisor page; input is the advisor root element and no value is returned. */
  constructor(root) {
    this.root = root;
    this.mode = root.dataset.mode;
    this.profileMode = this.mode === "five_elements";
    this.sessionStorageKey = `talisment:${this.mode}:consultation-v3`;
    this.apiOrigin = root.dataset.apiOrigin;
    this.bootstrapPath = root.dataset.bootstrapPath;
    this.session = null;
    this.messages = root.querySelector("[data-view], [data-messages]");
    this.conversationMessages = this.messages;
    this.status = root.querySelector("[data-status]");
    this.composer = root.querySelector("[data-composer]");
    this.input = root.querySelector("[data-input]");
    this.start = root.querySelector("[data-start]");
    this.giftStart = root.querySelector("[data-gift-start]");
    this.deleteButton = root.querySelector("[data-delete]");
    this.steps = [...root.querySelectorAll("[data-step]")];
    this.entryContext = this.readEntryContext();
    this.applyEntryPresentation();
    this.bind();
    this.restore();
  }

  /* Read only whitelisted, non-sensitive entry parameters; no input and returns a stable attribution object. */
  readEntryContext() {
    if (!this.profileMode) return {};
    const query = new URLSearchParams(window.location.search);
    const entryVariant = ["profile", "gift"].includes(query.get("entry"))
      ? query.get("entry")
      : "profile";
    const allowedFocus = new Set([
      "career_progress",
      "relationships",
      "focus_study",
      "change_beginning",
      "balance_peace",
      "general_guidance",
    ]);
    const requestedFocus = query.get("focus");
    const creative = query.get("creative") || "";
    return {
      entryVariant,
      ...(allowedFocus.has(requestedFocus)
        ? { entryFocus: requestedFocus }
        : {}),
      ...(/^[A-Za-z0-9_-]{1,64}$/.test(creative)
        ? { creativeId: creative }
        : {}),
    };
  }

  /* Adapt the landing copy to the approved entry path; no input and no return value. */
  applyEntryPresentation() {
    if (!this.profileMode) return;
    const title = this.root.querySelector("[data-entry-title]");
    const description = this.root.querySelector("[data-entry-description]");
    if (this.entryContext.entryVariant === "gift") {
      title.textContent = "Choose a gift with meaning behind every element.";
      description.textContent =
        "Use the birth details you know to explore a traditional Five Elements pattern, or shop the collection directly whenever a piece feels right.";
      this.root.classList.add("tc-profile--gift-entry");
    }
    this.emitAnalyticsEvent("five_elements_entry_viewed", {
      entry_variant: this.entryContext.entryVariant,
    });
  }

  /* Emit a de-identified storefront event; input is event name/properties and no value is returned. */
  emitAnalyticsEvent(eventName, properties = {}) {
    if (!this.profileMode || !eventName.startsWith("five_elements_")) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      entry_variant: this.entryContext.entryVariant || "profile",
      entry_focus: this.entryContext.entryFocus || "none",
      creative_id: this.entryContext.creativeId || "none",
      ...properties,
    });
  }

  /* Restore one unexpired browser session and its deterministic UI; no parameters and returns Promise<void>. */
  async restore() {
    const raw = sessionStorage.getItem(this.sessionStorageKey);
    if (!raw) return;
    try {
      this.session = JSON.parse(raw);
      const restored = await this.fetchCurrentSessionState();
      this.presentUiBlock(restored.uiBlock);
      this.setBusy(false, "");
    } catch (error) {
      if (
        error instanceof TalismentAdvisorRequestError &&
        [401, 404].includes(error.status)
      ) {
        sessionStorage.removeItem(this.sessionStorageKey);
        this.session = null;
        return;
      }
      this.renderRecoveryNotice();
      this.setBusy(false, "Your saved report could not be displayed yet.");
    }
  }

  /* Bind page actions, submit, Enter, and deletion; no parameters and no return value. */
  bind() {
    this.start?.addEventListener("click", () => this.begin("self"));
    this.giftStart?.addEventListener("click", () => this.begin("gift"));
    this.composer?.addEventListener("submit", (event) => {
      event.preventDefault();
      this.send();
    });
    this.input?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        if (!this.input.disabled) this.composer.requestSubmit();
      }
    });
    this.deleteButton?.addEventListener("click", () => this.deleteProfile());
  }

  /* Create a signed Shopify-backed session for one selected path; input is self/gift and returns Promise<void>. */
  async begin(path = "self") {
    this.setBusy(true, "");
    try {
      const route = `${this.bootstrapPath}?mode=${encodeURIComponent(this.mode)}`;
      const response = await fetch(route, { method: "POST" });
      if (!response.ok) throw new Error(`bootstrap_${response.status}`);
      this.session = await response.json();
      sessionStorage.setItem(
        this.sessionStorageKey,
        JSON.stringify(this.session),
      );
      const consent = await this.authorizedFetch(
        `/v1/sales/sessions/${this.session.sessionId}/consents`,
        {
          method: "POST",
          body: JSON.stringify({
            consentVersion: "traditional-culture-v2",
            purpose: this.mode,
            granted: true,
            scopeCodes: ["consultation_messages", "structured_profile"],
          }),
        },
      );
      if (!consent.ok) throw new Error(`consent_${consent.status}`);
      const stateResponse = await this.authorizedFetch(
        `/v1/sales/sessions/${this.session.sessionId}`,
      );
      if (!stateResponse.ok) throw new Error(`state_${stateResponse.status}`);
      const state = await stateResponse.json();
      this.session = { ...this.session, state: state.state };
      let nextBlock = state.uiBlock;
      if (this.profileMode) {
        const entryResult = await this.requestAction("record_entry_context", {
          entryVariant: path === "gift" ? "gift" : "profile",
          ...(this.entryContext.entryFocus
            ? { entryFocus: this.entryContext.entryFocus }
            : {}),
          ...(this.entryContext.creativeId
            ? { creativeId: this.entryContext.creativeId }
            : {}),
        });
        nextBlock = entryResult.uiBlock;
        const recipient = await this.requestAction("select_recipient", {
          value: path === "gift" ? "friend" : "self",
        });
        nextBlock = recipient.uiBlock;
        if (path === "gift") {
          const gift = await this.requestAction("select_friend_reason", {
            value: "gift",
          });
          nextBlock = gift.uiBlock;
        }
        this.emitAnalyticsEvent("five_elements_path_selected", {
          entry_variant: path === "gift" ? "gift" : "profile",
        });
      }
      this.presentUiBlock(nextBlock);
      this.deleteButton.hidden = false;
      this.setBusy(false, "");
    } catch (error) {
      this.setBusy(
        false,
        "The advisor is not available yet. Please try again later.",
      );
    }
  }

  /* Submit one structured option or form; input is action type and values, returns Promise<void>. */
  async submitAction(actionType, values) {
    if (!this.session) return;
    if (
      this.profileMode &&
      actionType === "submit_five_elements_report_profile"
    ) {
      values.outputLanguage = this.reportLanguage();
    }
    this.setBusy(
      true,
      actionType === "submit_five_elements_report_profile"
        ? "Your Five Elements report and matching pieces are being prepared…"
        : "",
    );
    let result;
    try {
      result = await this.requestAction(actionType, values);
    } catch (error) {
      if (await this.recoverCompletedReport(error)) return;
      this.setBusy(
        false,
        "That answer could not be saved. Please review it and try again.",
      );
      return;
    }
    try {
      this.presentUiBlock(result.uiBlock);
      this.setBusy(false, "");
      if (
        this.profileMode &&
        actionType === "submit_five_elements_report_profile"
      ) {
        this.emitAnalyticsEvent("five_elements_profile_submitted");
      }
      if (result.uiBlock?.openingPrompt) {
        await this.sendContent(result.uiBlock.openingPrompt, false);
      }
    } catch (error) {
      const recovered = await this.restoreSavedState();
      if (!recovered) {
        this.renderRecoveryNotice();
        this.setBusy(
          false,
          "Your report was saved, but it could not be displayed yet.",
        );
      }
    }
  }

  /* Detect the customer's preferred report language from the storefront and browser; no input and returns a simple English language name for the model. */
  reportLanguage() {
    const raw =
      `${document.documentElement.lang || ""} ${navigator.language || ""}`.toLowerCase();
    if (raw.includes("zh")) return "Chinese";
    if (raw.includes("es")) return "Spanish";
    if (raw.includes("fr")) return "French";
    if (raw.includes("de")) return "German";
    if (raw.includes("ja")) return "Japanese";
    if (raw.includes("ko")) return "Korean";
    return "English";
  }

  /* Send one deterministic structured action and persist returned state; input is action type/values and returns the parsed result. */
  async requestAction(actionType, values) {
    const response = await this.authorizedFetch(
      `/v1/sales/sessions/${this.session.sessionId}/actions`,
      {
        method: "POST",
        body: JSON.stringify({
          actionId: crypto.randomUUID(),
          actionType,
          values,
        }),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new TalismentAdvisorRequestError(response.status, body.error);
    }
    const result = await response.json();
    this.session = { ...this.session, state: result.state };
    sessionStorage.setItem(
      this.sessionStorageKey,
      JSON.stringify(this.session),
    );
    return result;
  }

  /* Read and persist the server-authoritative session state; no input and returns the parsed state payload. */
  async fetchCurrentSessionState() {
    const response = await this.authorizedFetch(
      `/v1/sales/sessions/${this.session.sessionId}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new TalismentAdvisorRequestError(response.status, body.error);
    }
    const restored = await response.json();
    this.session = { ...this.session, state: restored.state };
    sessionStorage.setItem(
      this.sessionStorageKey,
      JSON.stringify(this.session),
    );
    this.deleteButton.hidden = false;
    return restored;
  }

  /* Render a UI block off-screen and atomically commit content plus presentation state; input is one server UI block and no value is returned. */
  presentUiBlock(block) {
    if (!this.isSupportedUiBlock(block)) {
      throw new Error("advisor_ui_block_invalid");
    }
    const staging = document.createElement("div");
    const activeMessages = this.messages;
    const activeConversationMessages = this.conversationMessages;
    this.messages = staging;
    if (this.conversationMessages === activeMessages) {
      this.conversationMessages = staging;
    }
    try {
      this.renderUiBlock(block);
      if (!staging.childNodes.length) throw new Error("advisor_ui_block_empty");
    } finally {
      this.messages = activeMessages;
      this.conversationMessages = activeConversationMessages;
    }
    activeMessages.replaceChildren(...staging.childNodes);
    this.applyUiBlockPresentation(block);
    activeMessages.scrollTop =
      block?.blockType === "report" ? 0 : activeMessages.scrollHeight;
    if (block.blockType === "report") {
      this.emitAnalyticsEvent("five_elements_result_viewed", {
        analysis_tier: block.report?.analysisTier || "unknown",
        has_verified_match: this.objectItems(block.matchedProducts).length > 0,
      });
    }
  }

  /* Validate one server UI block before it can affect the live page; input is unknown block data and returns boolean. */
  isSupportedUiBlock(block) {
    return Boolean(
      block &&
      typeof block === "object" &&
      !Array.isArray(block) &&
      [
        "consultation-ui-v1",
        "consultation-ui-v2",
        "consultation-ui-v3",
      ].includes(block.version),
    );
  }

  /* Commit step, report-layout and composer state only after detached rendering succeeds; input is a validated UI block and no value is returned. */
  applyUiBlockPresentation(block) {
    this.updateSteps(block.step);
    this.root.classList.toggle(
      "tc-advisor--report",
      block.blockType === "report",
    );
    if (this.composer) this.composer.hidden = !block.allowConversation;
    if (this.composer && !this.composer.hidden) this.input?.focus();
  }

  /* Return only non-array JSON objects from an unknown list; input is unknown JSON and returns object array. */
  objectItems(value) {
    return Array.isArray(value)
      ? value.filter(
          (item) => item && typeof item === "object" && !Array.isArray(item),
        )
      : [];
  }

  /* Return only non-empty strings from an unknown list; input is unknown JSON and returns string array. */
  textItems(value) {
    return Array.isArray(value)
      ? value.filter((item) => typeof item === "string" && item.trim())
      : [];
  }

  /* Restore the latest saved server state after a client render problem; no input and returns whether display recovery succeeded. */
  async restoreSavedState() {
    try {
      const restored = await this.fetchCurrentSessionState();
      this.presentUiBlock(restored.uiBlock);
      this.setBusy(false, "");
      return true;
    } catch (error) {
      return false;
    }
  }

  /* Recover an already-completed report when a stale form is submitted again; input is one request error and returns whether recovery succeeded. */
  async recoverCompletedReport(error) {
    if (
      !(error instanceof TalismentAdvisorRequestError) ||
      error.status !== 409 ||
      ![
        "consultation_action_not_allowed",
        "sales_session_state_conflict",
      ].includes(error.code)
    ) {
      return false;
    }
    const recovered = await this.restoreSavedState();
    if (!recovered) {
      this.renderRecoveryNotice();
      this.setBusy(false, "Your saved report could not be displayed yet.");
    }
    return true;
  }

  /* Show a recoverable report-display notice without deleting the saved session; no input and no return value. */
  renderRecoveryNotice() {
    this.root.classList.remove("tc-advisor--report");
    if (this.composer) this.composer.hidden = true;
    const card = document.createElement("article");
    card.className = "tc-advisor__block tc-advisor__recovery";
    const title = document.createElement("h2");
    title.textContent = "Your report is saved";
    const text = document.createElement("p");
    text.textContent =
      "The report could not be displayed in this browser yet. Reload the saved result without entering your details again.";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "tc-advisor__primary";
    button.textContent = "Reload Saved Report";
    button.addEventListener("click", async () => {
      this.setBusy(true, "Reloading your saved report…");
      if (!(await this.restoreSavedState())) {
        this.setBusy(false, "Your saved report could not be displayed yet.");
      }
    });
    card.append(title, text, button);
    this.messages.replaceChildren(card);
  }

  /* Render one server-validated UI block; input is a block and no value is returned. */
  renderUiBlock(block) {
    if (!this.isSupportedUiBlock(block))
      throw new Error("advisor_ui_block_invalid");
    if (block.blockType === "report") {
      this.renderReport(block);
      return;
    }
    const card = document.createElement("article");
    card.className = `tc-advisor__block tc-advisor__block--${block.blockType}`;
    const heading = document.createElement("h2");
    heading.textContent = block.title;
    const description = document.createElement("p");
    description.textContent = block.description;
    card.append(heading, description);

    if (block.blockType === "single_select") {
      const options = document.createElement("div");
      options.className = "tc-advisor__choice-grid";
      for (const item of block.options || []) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tc-advisor__choice";
        const label = document.createElement("strong");
        label.textContent = item.label;
        const detail = document.createElement("span");
        detail.textContent = item.description || "";
        button.append(label, detail);
        button.addEventListener("click", () =>
          this.submitAction(block.actionType, { value: item.id }),
        );
        options.appendChild(button);
      }
      card.appendChild(options);
    }

    if (block.blockType === "form") {
      const form = document.createElement("form");
      form.className = "tc-advisor__structured-form";
      const controls = new Map();
      for (const item of block.fields || []) {
        const wrapper = document.createElement("label");
        wrapper.className = "tc-advisor__field";
        const label = document.createElement("span");
        label.textContent = item.label;
        let control;
        if (item.type === "select") {
          control = document.createElement("select");
          const placeholder = document.createElement("option");
          placeholder.value = "";
          placeholder.textContent = "Choose an option";
          placeholder.disabled = true;
          placeholder.selected = true;
          control.appendChild(placeholder);
          for (const value of item.options || []) {
            const option = document.createElement("option");
            option.value = value.id;
            option.textContent = value.label;
            control.appendChild(option);
          }
        } else {
          control = document.createElement("input");
          control.type = item.type;
          control.placeholder = item.placeholder || "";
        }
        control.name = item.id;
        control.required = Boolean(item.required);
        controls.set(item.id, { control, wrapper });
        wrapper.append(label, control);
        form.appendChild(wrapper);
      }
      const refreshBirthFieldVisibility = () => {
        const accuracy = controls.get("birthDateAccuracy")?.control.value;
        const timeKnown = controls.get("birthTimeKnown")?.control.value;
        const calendar = controls.get("calendarType")?.control.value;
        const dateEntry = controls.get("birthDate");
        const timeEntry = controls.get("birthTime");
        const cityEntry = controls.get("birthCity");
        const lunarEntry = controls.get("lunarMonthType");
        if (dateEntry) {
          const hidden = accuracy === "unknown";
          dateEntry.wrapper.hidden = hidden;
          dateEntry.control.required = !hidden;
          if (hidden) dateEntry.control.value = "";
        }
        if (timeEntry) {
          const hidden = !["yes", "exact", "approximate"].includes(timeKnown);
          timeEntry.wrapper.hidden = hidden;
          timeEntry.control.required = !hidden;
          if (hidden) timeEntry.control.value = "";
        }
        if (cityEntry) cityEntry.wrapper.hidden = accuracy === "unknown";
        if (lunarEntry) {
          const hidden = calendar !== "lunar";
          lunarEntry.wrapper.hidden = hidden;
          lunarEntry.control.required = !hidden && accuracy !== "unknown";
          if (hidden) lunarEntry.control.value = "";
        }
      };
      for (const id of [
        "birthDateAccuracy",
        "birthTimeKnown",
        "calendarType",
      ]) {
        controls
          .get(id)
          ?.control.addEventListener("change", refreshBirthFieldVisibility);
      }
      refreshBirthFieldVisibility();
      const focusControl = controls.get("focus")?.control;
      if (focusControl && this.entryContext.entryFocus) {
        focusControl.value = this.entryContext.entryFocus;
      }
      const submit = document.createElement("button");
      submit.type = "submit";
      submit.className = "tc-advisor__primary";
      submit.textContent = block.submitLabel || "Continue";
      form.appendChild(submit);
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const values = Object.fromEntries(new FormData(form).entries());
        this.submitAction(block.actionType, values);
      });
      card.appendChild(form);
    }

    if (block.blockType === "summary") {
      const list = document.createElement("dl");
      list.className = "tc-advisor__summary";
      for (const item of block.summaryItems || []) {
        const term = document.createElement("dt");
        term.textContent = item.label;
        const value = document.createElement("dd");
        value.textContent = this.humanize(item.value);
        list.append(term, value);
      }
      card.appendChild(list);
      if (block.guidanceSections?.length) {
        const guidance = document.createElement("div");
        guidance.className = "tc-advisor__guidance-grid";
        for (const section of block.guidanceSections) {
          const item = document.createElement("section");
          item.className = "tc-advisor__guidance-card";
          const label = document.createElement("span");
          label.textContent = section.label;
          const title = document.createElement("h3");
          title.textContent = section.title;
          const text = document.createElement("p");
          text.textContent = section.text;
          item.append(label, title, text);
          guidance.appendChild(item);
        }
        card.appendChild(guidance);
      }
      if (block.recommendationStatus === "demo_only" && block.demoProduct) {
        card.appendChild(this.renderDemoProduct(block.demoProduct));
      } else if (block.recommendationStatus === "pending_verified_data") {
        const notice = document.createElement("p");
        notice.className = "tc-advisor__pending";
        notice.textContent =
          "Product matches will appear here after the Five Elements rules and bead meanings are verified.";
        card.appendChild(notice);
      }
      if (block.actionType && block.actionLabel) {
        const continueButton = document.createElement("button");
        continueButton.type = "button";
        continueButton.className =
          "tc-advisor__primary tc-advisor__summary-action";
        continueButton.textContent = block.actionLabel;
        continueButton.addEventListener("click", () =>
          this.submitAction(block.actionType, { confirmed: "yes" }),
        );
        card.appendChild(continueButton);
      }
      if (block.suggestedQuestions?.length) {
        const questions = document.createElement("div");
        questions.className = "tc-advisor__suggested-questions";
        const title = document.createElement("strong");
        title.textContent = "Continue the conversation";
        questions.appendChild(title);
        for (const question of block.suggestedQuestions) {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = question;
          button.addEventListener("click", () => {
            this.input.value = question;
            this.input.focus();
          });
          questions.appendChild(button);
        }
        card.appendChild(questions);
      }
    }
    this.messages.appendChild(card);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  /* Render the report-first Five Elements result and same-page verified product area; input is a v3 report block and no value is returned. */
  renderReport(block) {
    const report =
      block.report &&
      typeof block.report === "object" &&
      !Array.isArray(block.report)
        ? block.report
        : {};
    const article = document.createElement("article");
    article.className = "tc-advisor__report";

    const reportHeader = document.createElement("header");
    reportHeader.className = "tc-advisor__report-header";
    const headerCopy = document.createElement("div");
    const eyebrow = document.createElement("span");
    eyebrow.textContent = report.analysisTierLabel || "Five Elements report";
    const title = document.createElement("h2");
    title.textContent = report.title || block.title;
    const focus = document.createElement("p");
    focus.textContent =
      "A complete cultural reading based on your birth details.";
    headerCopy.append(eyebrow, title, focus);
    const editButton = document.createElement("button");
    editButton.type = "button";
    editButton.className = "tc-advisor__text-button";
    editButton.textContent = "Edit details and recalculate";
    editButton.addEventListener("click", () => this.restartReport());
    reportHeader.append(headerCopy, editButton);

    const overview = document.createElement("section");
    overview.className = "tc-advisor__report-overview";
    overview.append(
      this.renderFiveElementsGraphic(report),
      this.renderReportSummary(report),
    );

    let products = null;
    const matchedProducts = this.objectItems(block.matchedProducts);
    if (matchedProducts.length) {
      products = document.createElement("section");
      products.className = "tc-advisor__report-section tc-advisor__products";
      const productsEyebrow = document.createElement("span");
      productsEyebrow.className = "tc-advisor__section-eyebrow";
      productsEyebrow.textContent = "Matching pieces";
      const productsTitle = document.createElement("h3");
      productsTitle.textContent = "Pieces selected to complement your report";
      const productsIntro = document.createElement("p");
      productsIntro.textContent =
        "These available pieces match the report's support direction through the Five Elements tags and bead meanings published with each product.";
      products.append(productsEyebrow, productsTitle, productsIntro);
      const productGrid = document.createElement("div");
      productGrid.className = "tc-advisor__product-grid";
      for (const product of matchedProducts.slice(0, 2)) {
        productGrid.appendChild(this.renderMatchedProduct(product));
      }
      products.appendChild(productGrid);
    }

    const dynamics = this.renderElementDynamics(report);
    const narrative = this.renderNarrativeReport(report);
    const guidance = this.renderFoundationGuidance(report);
    const interpretation = this.renderReportInterpretation(report);
    const reflections = this.renderReflectionPrompts(report);
    const browse = this.renderCollectionBrowse(Boolean(products));

    const details = document.createElement("section");
    details.className = "tc-advisor__report-section tc-advisor__report-details";
    const detailsEyebrow = document.createElement("span");
    detailsEyebrow.className = "tc-advisor__section-eyebrow";
    detailsEyebrow.textContent = "Report details";
    const detailsTitle = document.createElement("h3");
    detailsTitle.textContent = "How this foundation was calculated";
    details.append(detailsEyebrow, detailsTitle);
    details.appendChild(this.renderFoundationDetails(report));

    if (block.assistantAvailable) {
      const assistant = document.createElement("section");
      assistant.className =
        "tc-advisor__report-section tc-advisor__report-assistant";
      const assistantTitle = document.createElement("h3");
      assistantTitle.textContent = "Want a simpler explanation?";
      const assistantText = document.createElement("p");
      assistantText.textContent =
        "Ask about a term or section after you have read the foundation. This step is optional.";
      const assistantButton = document.createElement("button");
      assistantButton.type = "button";
      assistantButton.className = "tc-advisor__primary";
      assistantButton.textContent = "Explain My Foundation";
      assistantButton.addEventListener("click", () =>
        this.openReportAssistant(assistant),
      );
      assistant.append(assistantTitle, assistantText, assistantButton);
      details.appendChild(assistant);
    }

    article.append(reportHeader, overview);
    if (dynamics) article.appendChild(dynamics);
    if (narrative) article.appendChild(narrative);
    if (guidance) article.appendChild(guidance);
    if (interpretation) article.appendChild(interpretation);
    if (reflections) article.appendChild(reflections);
    if (products) article.appendChild(products);
    article.appendChild(browse);
    article.appendChild(details);
    this.messages.appendChild(article);
    this.messages.scrollTop = 0;
  }

  /* Render the deterministic strong/weak Five Elements result; input is report data and returns an HTMLElement or null. */
  renderElementDynamics(report) {
    const analysis = report.strengthAnalysis || {};
    const dynamics = this.objectItems(analysis.elementDynamics);
    if (!dynamics.length) return null;
    const section = document.createElement("section");
    section.className =
      "tc-advisor__report-section tc-advisor__element-dynamics";
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = "Five Elements dynamics";
    const title = document.createElement("h3");
    title.textContent = `Your core pattern: ${this.humanize(analysis.corePattern || "five elements balance")}`;
    const intro = document.createElement("p");
    intro.textContent = `Your Day Master is assessed as ${this.humanize(analysis.dayMasterStrength || "balanced")}. The chart shows how each element is supported by visible stems, branches, hidden stems and the birth season.`;
    const grid = document.createElement("div");
    grid.className = "tc-advisor__dynamics-grid";
    for (const item of dynamics) {
      const card = document.createElement("article");
      card.dataset.element = item.element || "";
      const label = document.createElement("span");
      label.textContent = this.humanize(item.strength || "balanced");
      const heading = document.createElement("h4");
      heading.textContent = this.humanize(item.element || "element");
      const relationship = document.createElement("p");
      relationship.textContent = `${this.humanize(item.relationshipToDayMaster || "relationship")} relationship to the Day Master${item.isSeasonElement ? " · season-supported" : ""}.`;
      card.append(label, heading, relationship);
      grid.appendChild(card);
    }
    section.append(eyebrow, title, intro, grid);
    return section;
  }

  /* Render the AI-written full reading as human-readable chapters; input is report data and returns an HTMLElement or null. */
  renderNarrativeReport(report) {
    const narrative = report.narrative || {};
    const chapters = [
      ["Element overview", narrative.elementOverview],
      ["Personality and natural tendencies", narrative.personality],
      ["Career and direction", narrative.career],
      ["Love and partnership", narrative.relationships],
      ["Family and home", narrative.family],
      ["Friends and social connection", narrative.friendships],
      ["Five Elements support plan", narrative.supportPlan],
    ].filter(([, paragraphs]) => this.textItems(paragraphs).length);
    if (!chapters.length) return null;
    const section = document.createElement("section");
    section.className = "tc-advisor__report-section tc-advisor__narrative";
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = "Your complete reading";
    const title = document.createElement("h3");
    title.textContent =
      narrative.title || "Understanding your Five Elements pattern";
    section.append(eyebrow, title);
    for (const [headingText, paragraphs] of chapters) {
      const chapter = document.createElement("article");
      const heading = document.createElement("h4");
      heading.textContent = headingText;
      chapter.appendChild(heading);
      for (const paragraphText of this.textItems(paragraphs)) {
        const paragraph = document.createElement("p");
        paragraph.textContent = paragraphText;
        chapter.appendChild(paragraph);
      }
      section.appendChild(chapter);
    }
    if (
      typeof narrative.closingSummary === "string" &&
      narrative.closingSummary
    ) {
      const closing = document.createElement("blockquote");
      closing.textContent = narrative.closingSummary;
      section.appendChild(closing);
    }
    return section;
  }

  /* Render customer-facing explanations of calculated roles; input is report data and returns an HTMLElement or null. */
  renderFoundationGuidance(report) {
    if (!this.objectItems(report.foundationGuidance).length) {
      return null;
    }
    const section = document.createElement("section");
    section.className = "tc-advisor__report-section tc-profile__understanding";
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = "Understanding the foundation";
    const title = document.createElement("h3");
    title.textContent = "What these chart roles mean";
    const grid = document.createElement("div");
    grid.className = "tc-profile__explanation-grid";
    for (const item of this.objectItems(report.foundationGuidance)) {
      const card = document.createElement("article");
      const heading = document.createElement("h4");
      heading.textContent = item.title || "Chart role";
      const text = document.createElement("p");
      text.textContent = item.text || "";
      card.append(heading, text);
      grid.appendChild(card);
    }
    section.append(eyebrow, title, grid);
    return section;
  }

  /* Render focus-ordered reflection prompts without presenting them as chart conclusions; input is report data and returns an HTMLElement or null. */
  renderReflectionPrompts(report) {
    if (!this.textItems(report.reflectionPrompts).length) {
      return null;
    }
    const section = document.createElement("section");
    section.className = "tc-advisor__report-section tc-profile__reflections";
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = "A practical next look";
    const title = document.createElement("h3");
    title.textContent = `Reflect on ${this.humanize(report.focus || "general guidance")}`;
    const intro = document.createElement("p");
    intro.textContent =
      "These prompts organize your chosen focus. They do not change the calculated chart facts.";
    const list = document.createElement("ol");
    for (const prompt of this.textItems(report.reflectionPrompts)) {
      const item = document.createElement("li");
      item.textContent = prompt;
      list.appendChild(item);
    }
    section.append(eyebrow, title, intro, list);
    return section;
  }

  /* Render a normal collection path that never claims personal matching; input states whether verified matches exist and returns an HTMLElement. */
  renderCollectionBrowse(hasVerifiedMatches) {
    const section = document.createElement("section");
    section.className = "tc-advisor__report-section tc-profile__browse";
    const copy = document.createElement("div");
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = hasVerifiedMatches
      ? "Explore more"
      : "Shop in your own way";
    const title = document.createElement("h3");
    title.textContent = hasVerifiedMatches
      ? "Browse more Five Elements pieces"
      : "You do not need a report to choose a piece";
    const text = document.createElement("p");
    text.textContent = hasVerifiedMatches
      ? "Continue exploring by material, meaning and design."
      : "Explore the collection by design and cultural meaning whenever a piece speaks to you.";
    copy.append(eyebrow, title, text);
    const link = document.createElement("a");
    link.className = "tc-advisor__primary";
    link.href = "/collections/all";
    link.textContent = "Browse Five Elements Pieces";
    section.append(copy, link);
    return section;
  }

  /* Render a deterministic Five Elements summary graphic; input is report data and returns an HTMLElement. */
  renderFiveElementsGraphic(report) {
    const figure = document.createElement("figure");
    figure.className = "tc-advisor__element-graphic";
    const caption = document.createElement("figcaption");
    caption.textContent = "Five Elements chart foundation";
    const elements = document.createElement("div");
    elements.className = "tc-advisor__element-map";
    const dynamics = this.objectItems(report.strengthAnalysis?.elementDynamics);
    const reportElements = this.objectItems(report.elements);
    for (const item of reportElements) {
      const dynamic = dynamics.find((entry) => entry.element === item.id) || {};
      const element = document.createElement("div");
      element.className = "tc-advisor__element-node";
      element.style.setProperty("--element-color", item.color || "#833f3f");
      if (item.isDayMaster) element.classList.add("is-day-master");
      if (item.isSeason) element.classList.add("is-season");
      const name = document.createElement("strong");
      name.textContent = item.label || item.id;
      const meaning = document.createElement("span");
      meaning.textContent = item.meaning || "";
      const markers = document.createElement("small");
      markers.textContent = [
        dynamic.strength ? this.humanize(dynamic.strength) : "",
        item.isDayMaster ? "Day Master" : "",
        item.isSeason ? "Season" : "",
      ]
        .filter(Boolean)
        .join(" · ");
      element.append(name, meaning, markers);
      elements.appendChild(element);
    }
    const note = document.createElement("p");
    note.textContent =
      "The strength labels combine the chart structure with the seasonal setting. They are used as the stable foundation for the written interpretation.";
    figure.append(caption, elements, note);
    return figure;
  }

  /* Render the plain-language result alongside its graphic; input is report data and returns an HTMLElement. */
  renderReportSummary(report) {
    const section = document.createElement("section");
    section.className = "tc-advisor__result-summary";
    const eyebrow = document.createElement("span");
    eyebrow.textContent = "Your result in plain language";
    const title = document.createElement("h3");
    title.textContent = report.headline || "Your report foundation is ready.";
    const text = document.createElement("div");
    text.className = "tc-advisor__executive-summary";
    const narrativeSummary = this.textItems(report.narrative?.executiveSummary);
    const summary = narrativeSummary.length
      ? narrativeSummary
      : [typeof report.plainLanguage === "string" ? report.plainLanguage : ""];
    for (const paragraphText of summary) {
      const paragraph = document.createElement("p");
      paragraph.textContent = paragraphText;
      text.appendChild(paragraph);
    }
    const facts = document.createElement("dl");
    const foundation = report.foundation || {};
    for (const [label, value] of [
      ["Day Master", foundation.dayMaster],
      ["Season", foundation.season],
      ["Pillars", foundation.pillars],
    ]) {
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = value || "Not available";
      facts.append(term, description);
    }
    section.append(eyebrow, title, text, facts);
    return section;
  }

  /* Render only approved support and life-theme conclusions; input is report data and returns an HTMLElement or null. */
  renderReportInterpretation(report) {
    const supportThemes = this.objectItems(report.supportThemes);
    const lifeThemes = this.objectItems(report.lifeThemes);
    if (!supportThemes.length && !lifeThemes.length) return null;
    const section = document.createElement("section");
    section.className =
      "tc-advisor__report-section tc-advisor__report-interpretation";
    const eyebrow = document.createElement("span");
    eyebrow.className = "tc-advisor__section-eyebrow";
    eyebrow.textContent = "Approved cultural interpretation";
    const title = document.createElement("h3");
    title.textContent = "Support themes and life reflections";
    section.append(eyebrow, title);
    if (supportThemes.length) {
      const supportGrid = document.createElement("div");
      supportGrid.className = "tc-advisor__support-grid";
      for (const theme of supportThemes.slice(0, 3)) {
        const card = document.createElement("article");
        const label = document.createElement("span");
        label.textContent = theme.priority || "Support theme";
        const heading = document.createElement("h4");
        heading.textContent =
          theme.title || theme.element || "Cultural support";
        const text = document.createElement("p");
        text.textContent = theme.explanation || "";
        const reason = document.createElement("small");
        reason.textContent = theme.reasonCode
          ? `Evidence: ${theme.reasonCode}`
          : "";
        card.append(label, heading, text, reason);
        supportGrid.appendChild(card);
      }
      section.appendChild(supportGrid);
    }
    if (lifeThemes.length) {
      const themeList = document.createElement("div");
      themeList.className = "tc-advisor__life-themes";
      for (const theme of lifeThemes) {
        const details = document.createElement("details");
        const summary = document.createElement("summary");
        summary.textContent = theme.title || this.humanize(theme.domain);
        const body = document.createElement("div");
        for (const [label, value] of [
          ["Chart basis", theme.observedFact],
          ["Cultural interpretation", theme.interpretation],
          ["Support theme", theme.supportTheme],
          ["Practical reflection", theme.reflection],
        ]) {
          if (!value) continue;
          const row = document.createElement("p");
          const strong = document.createElement("strong");
          strong.textContent = `${label}: `;
          row.append(strong, document.createTextNode(value));
          body.appendChild(row);
        }
        details.append(summary, body);
        themeList.appendChild(details);
      }
      section.appendChild(themeList);
    }
    return section;
  }

  /* Render expandable chart facts and limitations; input is report data and returns an HTMLElement. */
  renderFoundationDetails(report) {
    const details = document.createElement("details");
    details.className = "tc-advisor__foundation-details";
    const summary = document.createElement("summary");
    summary.textContent = "View data completeness and important limits";
    const body = document.createElement("div");
    body.className = "tc-advisor__foundation-body";
    const tier = document.createElement("p");
    tier.textContent = `Foundation level: ${report.analysisTierLabel || this.humanize(report.analysisTier)}`;
    body.append(tier);
    const limits = this.textItems(report.limits);
    if (limits.length) {
      const list = document.createElement("ul");
      for (const limit of limits) {
        const item = document.createElement("li");
        item.textContent = limit;
        list.appendChild(item);
      }
      body.appendChild(list);
    }
    details.append(summary, body);
    return details;
  }

  /* Render one verified same-page product match; input is server-validated product data and returns an HTMLElement. */
  renderMatchedProduct(product) {
    const article = document.createElement("article");
    article.className = "tc-advisor__matched-product";
    if (product.imageUrl) {
      const image = document.createElement("img");
      image.src = product.imageUrl;
      image.alt =
        product.imageAlt ||
        product.title ||
        product.name ||
        "Matched Talisment piece";
      image.loading = "lazy";
      article.appendChild(image);
    }
    const body = document.createElement("div");
    const badge = document.createElement("span");
    badge.textContent =
      product.rank === "primary" ? "Primary match" : "Alternative";
    const title = document.createElement("h4");
    title.textContent = product.title || product.name || "Talisment piece";
    const price = document.createElement("p");
    price.textContent = this.formatProductPrice(product);
    const reason = document.createElement("p");
    reason.textContent = product.matchReason || "";
    body.append(badge, title, price, reason);
    if (product.culturalSupportPrinciple) {
      const principle = document.createElement("p");
      const principleLabel = document.createElement("strong");
      principleLabel.textContent = "Five Elements principle: ";
      principle.append(
        principleLabel,
        document.createTextNode(product.culturalSupportPrinciple),
      );
      body.appendChild(principle);
    }
    if (product.meaning) {
      const meaning = document.createElement("p");
      const meaningLabel = document.createElement("strong");
      meaningLabel.textContent = "Meaning: ";
      meaning.append(meaningLabel, document.createTextNode(product.meaning));
      body.appendChild(meaning);
    }
    const beads = this.objectItems(product.beads);
    if (beads.length) {
      const details = document.createElement("details");
      const summary = document.createElement("summary");
      summary.textContent = "Bead composition and cultural meaning";
      const list = document.createElement("ul");
      for (const bead of beads) {
        const item = document.createElement("li");
        item.textContent = `${bead.name}${bead.quantity ? ` × ${bead.quantity}` : ""}${bead.element ? ` · ${bead.element}` : ""} · ${bead.meaning}`;
        list.appendChild(item);
      }
      details.append(summary, list);
      body.appendChild(details);
    }
    const productUrl = this.approvedProductUrl(product.productUrl);
    if (productUrl) {
      const link = document.createElement("a");
      link.className = "tc-advisor__primary";
      link.href = productUrl;
      link.textContent = "View this piece";
      link.addEventListener("click", () =>
        this.emitAnalyticsEvent("five_elements_product_clicked", {
          recommendation_rank: product.rank || "unknown",
        }),
      );
      body.appendChild(link);
    }
    if (product.inventoryLabel) {
      const availability = document.createElement("small");
      availability.className = "tc-advisor__product-availability";
      availability.textContent = product.inventoryLabel;
      body.appendChild(availability);
    }
    article.appendChild(body);
    return article;
  }

  /* Validate one Shopify product path before rendering a purchase link; input is an unknown URL and returns a same-origin product path or an empty string. */
  approvedProductUrl(value) {
    if (typeof value !== "string" || !value.trim()) return "";
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return "";
      if (!url.pathname.startsWith("/products/")) return "";
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return "";
    }
  }

  /* Format current Shopify price without allowing malformed product data to break the report; input is one product and returns display text. */
  formatProductPrice(product) {
    if (!product?.price) return product?.priceLabel || "View current price";
    const numericPrice = Number(product.price);
    if (!Number.isFinite(numericPrice)) {
      return product.priceLabel || "View current price";
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(product.currency || "")
          ? product.currency
          : "USD",
      }).format(numericPrice);
    } catch (error) {
      return product.priceLabel || `${numericPrice.toFixed(2)} USD`;
    }
  }

  /* Restart only the report browser session after explicit confirmation; no input and no value returned. */
  restartReport() {
    if (!window.confirm("Start a new report with corrected details?")) return;
    sessionStorage.removeItem(this.sessionStorageKey);
    window.location.reload();
  }

  /* Open one optional report-bound explanation panel; input is the host section and no value is returned. */
  openReportAssistant(host) {
    const existing = host.querySelector("[data-report-assistant]");
    if (existing) {
      existing.querySelector("textarea")?.focus();
      return;
    }
    const panel = document.createElement("div");
    panel.className = "tc-profile__assistant-panel";
    panel.dataset.reportAssistant = "true";
    const messages = document.createElement("div");
    messages.className = "tc-profile__assistant-messages";
    const form = document.createElement("form");
    form.className = "tc-profile__assistant-form";
    const input = document.createElement("textarea");
    input.rows = 2;
    input.maxLength = 4000;
    input.placeholder = "Ask about a term or section in this foundation...";
    input.setAttribute("aria-label", "Question about this foundation");
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "tc-advisor__primary";
    submit.textContent = "Ask";
    form.append(input, submit);
    panel.append(messages, form);
    host.appendChild(panel);
    this.composer = form;
    this.input = input;
    this.conversationMessages = messages;
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.send();
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        if (!input.disabled) form.requestSubmit();
      }
    });
    input.focus();
  }

  /* Render the non-purchasable demonstration product; input is validated server data and returns an HTMLElement. */
  renderDemoProduct(product) {
    const article = document.createElement("article");
    article.className = "tc-advisor__demo-product";

    const header = document.createElement("header");
    header.className = "tc-advisor__demo-header";
    const identity = document.createElement("div");
    const badge = document.createElement("span");
    badge.className = "tc-advisor__demo-badge";
    badge.textContent = product.badge;
    const name = document.createElement("h3");
    name.textContent = product.name;
    identity.append(badge, name);
    const price = document.createElement("p");
    price.className = "tc-advisor__demo-price";
    price.textContent = product.priceLabel;
    header.append(identity, price);

    const body = document.createElement("div");
    body.className = "tc-advisor__demo-body";
    const summary = document.createElement("p");
    summary.textContent = product.summary;
    const fit = document.createElement("p");
    fit.textContent = product.fitNote;
    const elements = document.createElement("div");
    elements.className = "tc-advisor__element-strip";
    for (const profile of product.elementProfile || []) {
      const element = document.createElement("span");
      element.className = "tc-advisor__element";
      element.style.setProperty("--element-color", profile.color);
      element.textContent = profile.element;
      element.title = profile.role;
      elements.appendChild(element);
    }
    body.append(summary, fit, elements);

    const details = document.createElement("details");
    const detailsTitle = document.createElement("summary");
    detailsTitle.textContent = "View bead-by-bead details";
    const beads = document.createElement("ol");
    beads.className = "tc-advisor__bead-list";
    for (const bead of product.beads || []) {
      const item = document.createElement("li");
      const beadName = document.createElement("strong");
      beadName.textContent = `${bead.name} · ${bead.element} × ${bead.quantity}`;
      const meaning = document.createElement("span");
      meaning.textContent = bead.meaning;
      item.append(beadName, meaning);
      beads.appendChild(item);
    }
    const note = document.createElement("p");
    note.className = "tc-advisor__demo-note";
    note.textContent = `${product.culturalNote} ${product.availability}`;
    details.append(detailsTitle, beads, note);
    article.append(header, body, details);
    return article;
  }

  /* Convert a stable ID into readable fallback text; input is an unknown value and returns a string. */
  humanize(value) {
    return String(value || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  /* Update the visual progress list; input is a stable step ID and no value is returned. */
  updateSteps(step) {
    const activeIndex = this.steps.findIndex(
      (item) => item.dataset.step === step,
    );
    this.steps.forEach((item, index) => {
      item.classList.toggle("is-current", index === activeIndex);
      item.classList.toggle(
        "is-complete",
        activeIndex >= 0 && index < activeIndex,
      );
    });
  }

  /* Send one post-consultation customer message; no parameters and returns Promise<void>. */
  async send() {
    const content = this.input.value.trim();
    if (!content || !this.session) return;
    this.input.value = "";
    await this.sendContent(content, true);
  }

  /* Send customer or server-approved opening text; input is content/visibility and returns Promise<void>. */
  async sendContent(content, showUserMessage) {
    if (!content || !this.session) return;
    if (showUserMessage) this.appendMessage("user", content);
    this.setBusy(true, "The advisor is preparing a reply…");
    try {
      const response = await this.authorizedFetch(
        `/v1/sales/sessions/${this.session.sessionId}/messages`,
        {
          method: "POST",
          body: JSON.stringify({ messageId: crypto.randomUUID(), content }),
        },
      );
      if (!response.ok) throw new Error(`message_${response.status}`);
      const run = await response.json();
      const events = await this.authorizedFetch(
        `/v1/sales/sessions/${this.session.sessionId}/runs/${run.runId}/events`,
      );
      if (!events.ok) throw new Error(`events_${events.status}`);
      const text = await events.text();
      for (const block of text.split("\n\n")) {
        const eventName = block.match(/^event: (.+)$/m)?.[1];
        const data = block.match(/^data: (.+)$/m)?.[1];
        if (eventName === "assistant_delta" && data) {
          this.appendMessage("assistant", JSON.parse(data).content);
        }
      }
      this.setBusy(false, "Reply complete.");
    } catch (error) {
      this.setBusy(
        false,
        "The reply could not be completed. Please try again later.",
      );
    }
  }

  /* Delete the current online profile; no parameters and returns Promise<void>. */
  async deleteProfile() {
    if (!this.session) return;
    const response = await this.authorizedFetch(
      `/v1/sales/sessions/${this.session.sessionId}/profile`,
      { method: "DELETE" },
    );
    if (response.ok) {
      sessionStorage.removeItem(this.sessionStorageKey);
      this.session = null;
      if (this.composer) this.composer.hidden = true;
      this.deleteButton.hidden = true;
      if (this.profileMode) {
        window.location.reload();
      } else {
        this.setBusy(false, "Your deletion request was recorded.");
      }
    }
  }

  /* Call the fixed API origin with the short bearer token; input is path/options and returns Response. */
  authorizedFetch(path, options = {}) {
    return fetch(`${this.apiOrigin}${path}`, {
      ...options,
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.session.token}`,
        ...options.headers,
      },
    });
  }

  /* Append escaped free-text content; input is role/content and no return value. */
  appendMessage(role, content) {
    const article = document.createElement("article");
    article.className = `tc-advisor__message tc-advisor__message--${role}`;
    article.textContent = content;
    this.conversationMessages.appendChild(article);
    this.conversationMessages.scrollTop =
      this.conversationMessages.scrollHeight;
  }

  /* Update busy state and status; input is boolean/text and no return value. */
  setBusy(busy, text) {
    this.root.setAttribute("aria-busy", busy ? "true" : "false");
    if (this.status) {
      this.status.textContent = text;
      this.status.hidden = !text;
    }
    if (this.input) this.input.disabled = busy;
    const button = this.composer?.querySelector('button[type="submit"]');
    if (button) button.disabled = busy;
    for (const buttonElement of this.messages.querySelectorAll("button")) {
      buttonElement.disabled = busy;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("[data-tc-advisor]")
    .forEach((root) => new TalismentTraditionalCultureAdvisor(root));
});
